import type { ButtonHTMLAttributes, ReactNode } from 'react'

import { itemState } from '../model/inventory'
import type { CollectionItem } from '../model/types'
import styles from './ui.module.css'

/** Шрифтовая метка-логотип. */
export function Wordmark(): ReactNode {
  return (
    <span className="label" style={{ letterSpacing: '0.18em' }}>
      КАРДБАЗАР
    </span>
  )
}

type ButtonKind = 'primary' | 'secondary'

interface ButtonProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className'> {
  kind?: ButtonKind
}

export function Button({
  kind = 'secondary',
  type = 'button',
  children,
  ...rest
}: ButtonProps): ReactNode {
  return (
    <button
      {...rest}
      type={type}
      className={[
        styles.button,
        kind === 'primary' ? styles.buttonPrimary : ''
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {children}
    </button>
  )
}

export function Panel({
  children
}: {
  children: ReactNode
}): ReactNode {
  return <div className={styles.panel}>{children}</div>
}

export function Row({
  children
}: {
  children: ReactNode
}): ReactNode {
  return <div className={styles.row}>{children}</div>
}

export function EmptyState({
  text
}: {
  text: string
}): ReactNode {
  return <div className={styles.emptyState}>{text}</div>
}

// MARK: - Секции

export function Section({
  children,
  gap
}: {
  children: ReactNode
  gap?: number
}): ReactNode {
  return (
    <section className={styles.section} style={{ gap }}>
      {children}
    </section>
  )
}

export function SectionHeader({
  title,
  trailing
}: {
  title: string
  /** Правый край: счётчик или короткий статус раздела. */
  trailing?: string
}): ReactNode {
  return (
    <div className={styles.sectionHeader}>
      <span className="label">{title}</span>
      {trailing !== undefined && (
        <span className="label" style={{ color: 'var(--foreground)' }}>
          {trailing}
        </span>
      )}
    </div>
  )
}

export function Divider(): ReactNode {
  return <hr className={styles.dividerLine} />
}

/** Шапка экрана: заголовок слева, действие справа. */
export function ScreenHeader({
  title,
  subtitle,
  action
}: {
  title: string
  subtitle?: string
  action?: ReactNode
}): ReactNode {
  return (
    <div className={styles.screenHeader}>
      <div className={styles.screenHeaderText}>
        <h1 className={styles.screenHeaderTitle}>{title}</h1>
        {subtitle !== undefined && (
          <span className="label">{subtitle}</span>
        )}
      </div>
      {action}
    </div>
  )
}

// MARK: - Сводка

/** Крупная цифра с подписью. */
export function Stat({
  value,
  caption
}: {
  value: string
  caption: string
}): ReactNode {
  return (
    <div className={styles.stat}>
      <span className={styles.statValue}>{value}</span>
      <span className="label">{caption}</span>
    </div>
  )
}

export function StatRow({
  children
}: {
  children: ReactNode
}): ReactNode {
  return <div className={styles.statRow}>{children}</div>
}

/** Шкала заполнения: значение от 0 до 1, всё что выше — обрезается. */
export function Progress({ value }: { value: number }): ReactNode {
  return (
    <div className={styles.progress}>
      <div
        className={styles.progressFill}
        style={{ width: `${Math.round(Math.min(1, Math.max(0, value)) * 100)}%` }}
      />
    </div>
  )
}

export function KeyValueRow({
  label,
  value,
  emphasis = false
}: {
  label: string
  value: string
  emphasis?: boolean
}): ReactNode {
  return (
    <div className={styles.keyValue}>
      <span className="label">{label}</span>
      <span
        className={[
          styles.keyValueValue,
          emphasis ? styles.keyValueEmphasis : ''
        ]
          .filter(Boolean)
          .join(' ')}
      >
        {value}
      </span>
    </div>
  )
}

/**
 * Клетка наклейки в сетке коллекции.
 *
 * Состояние различается заливкой и рамкой, а не цветом: интерфейс
 * монохромный, и в сетке 56px подпись всё равно не прочитать.
 */
export function ItemCell({
  item,
  onToggle
}: {
  item: CollectionItem
  onToggle?: (item: CollectionItem) => void
}): ReactNode {
  const state = itemState(item)
  const stateClass =
    state === 'duplicate'
      ? styles.itemDuplicate
      : state === 'owned'
        ? styles.itemOwned
        : item.isWanted
          ? styles.itemWanted
          : ''

  return (
    <button
      type="button"
      onClick={() => onToggle?.(item)}
      className={[styles.item, stateClass].filter(Boolean).join(' ')}
      title={item.title ?? item.number}
    >
      {item.number}
    </button>
  )
}
